/**
 * PodcastCard component with horizontal layout and video preview.
 * Features a tape reel animation that triggers when the card scrolls into view.
 * Based on Figma design for Podcasts media type.
 */

import React, { FC } from 'react';
import { styled } from 'linaria/react';
import tapeReelImage from '../../../assets/images/podcast-tape-reel.png';
import { FONTS } from '../../../styles/constants';
import { useCardAnimation } from '../../../hooks/useCardAnimation';
import { PodcastCardProps } from '../types';
import {
  DIMENSIONS,
  BORDER_RADIUS,
  TYPOGRAPHY,
  CARD_COLORS,
  CARD_ENTRANCE,
  OPACITY,
  PODCAST_ANIMATION,
} from '../styled/tokens';

/* ─── layout containers ─── */

const StyledPodcastContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  overflow: visible;
`;

const StyledLink = styled.a`
  display: block;
  text-decoration: none;
  cursor: pointer;
  color: inherit;
`;

/* ─── header (title only) ─── */

const StyledHeader = styled.div<{ isAnimating: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 12px;
  opacity: ${({ isAnimating }) => (isAnimating ? 1 : CARD_ENTRANCE.titleOpacity)};
  transition: opacity ${CARD_ENTRANCE.opacityDuration}s ${CARD_ENTRANCE.easing};
`;

const StyledTitle = styled.div`
  font-family: ${FONTS.primary};
  font-size: ${TYPOGRAPHY.podcastTitle.fontSize}px;
  line-height: 1.4;
  color: var(--card-text-primary, #181800);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* ─── episode preview area ─── */

const StyledPreviewContainer = styled.div<{ isEntranceActive: boolean }>`
  position: relative;
  overflow: visible;
  margin: 0 16px;
  padding-top: 12px;
  opacity: ${({ isEntranceActive }) => (isEntranceActive ? 1 : CARD_ENTRANCE.contentOpacity)};
  transform: ${({ isEntranceActive }) =>
    isEntranceActive
      ? 'translateY(0) scale(1)'
      : `translateY(${CARD_ENTRANCE.contentOffsetY}px) scale(${CARD_ENTRANCE.contentInitialScale})`};
  transition: opacity ${CARD_ENTRANCE.opacityDuration}s ${CARD_ENTRANCE.easing},
    transform ${CARD_ENTRANCE.scaleDuration}s ${CARD_ENTRANCE.easing};
  will-change: opacity, transform;
`;

/* ─── blurred background ─── */

const StyledBlurredBackground = styled.div<{ bgImage?: string; isDynamic: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: ${BORDER_RADIUS.lg}px;
  z-index: 0;
  pointer-events: none;
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-size: cover;
  background-position: center;
  opacity: ${OPACITY.podcastBlur};
  filter: blur(
    ${({ isDynamic }) => (isDynamic ? PODCAST_ANIMATION.blurExpandedFilter : PODCAST_ANIMATION.blurInitialFilter)}px
  );
  transition: filter ${PODCAST_ANIMATION.blurDuration}s ease;
`;

/* ─── cover image (slides/rotates/scales in dynamic state) ─── */

const StyledCoverImage = styled.div<{ isDynamic: boolean }>`
  position: relative;
  z-index: 1;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: ${BORDER_RADIUS.lg}px;
  overflow: hidden;
  transform: ${({ isDynamic }) =>
    isDynamic
      ? `translateX(${PODCAST_ANIMATION.coverSlideX}px) rotate(${PODCAST_ANIMATION.coverRotation}deg) scale(${PODCAST_ANIMATION.coverScaleEnd})`
      : 'translateX(0) rotate(0deg) scale(1)'};
  transition: transform ${PODCAST_ANIMATION.coverDuration}s ${PODCAST_ANIMATION.coverEasing};
  will-change: transform;

  &[data-video='true'] {
    aspect-ratio: 57 / 32;
  }
`;

const StyledVideoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* ─── tape reel ─── */

const StyledTapeReelContainer = styled.div<{ isDynamic: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 0;
  pointer-events: none;
  transform: ${({ isDynamic }) =>
    isDynamic
      ? `translate(calc(-50% + ${PODCAST_ANIMATION.reelEndX}px), calc(-50% + ${PODCAST_ANIMATION.reelEndY}px)) scale(1)`
      : 'translate(-50%, -50%) scale(0.7)'};
  transition: transform ${PODCAST_ANIMATION.reelDuration}s ${PODCAST_ANIMATION.reelEasing};
  will-change: transform;
`;

const StyledTapeReelImage = styled.img`
  display: block;

  @keyframes podcastReelSpin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  &[data-spinning='true'] {
    animation: podcastReelSpin ${PODCAST_ANIMATION.reelRotationDuration}s linear infinite;
  }
`;

/* ─── overlays ─── */

const StyledDuration = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: ${CARD_COLORS.overlayDark};
  border-radius: ${BORDER_RADIUS.sm}px;
  padding: 4px 8px;
  font-family: ${FONTS.secondary};
  font-size: ${TYPOGRAPHY.timestamp.fontSize}px;
  font-weight: ${TYPOGRAPHY.timestamp.fontWeight};
  color: ${CARD_COLORS.textLight};
  z-index: 2;
`;

const StyledChapterPill = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: ${CARD_COLORS.overlayDark};
  border-radius: ${BORDER_RADIUS.xl}px;
  padding: 6px 12px;
  font-family: ${FONTS.secondary};
  font-size: ${TYPOGRAPHY.timestamp.fontSize}px;
  font-weight: ${TYPOGRAPHY.timestamp.fontWeight};
  color: ${CARD_COLORS.textLight};
  white-space: nowrap;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 2;
`;

/* ─── mute button ─── */

const StyledMuteButton = styled.button<{ isDynamic: boolean }>`
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: ${DIMENSIONS.podcast.playButtonSize}px;
  height: ${DIMENSIONS.podcast.playButtonSize}px;
  border-radius: 50%;
  border: none;
  background: ${CARD_COLORS.overlayDark};
  color: ${CARD_COLORS.textLight};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  transform: ${({ isDynamic }) => (isDynamic ? 'scale(1)' : 'scale(0)')};
  transition: transform ${PODCAST_ANIMATION.muteButtonDuration}s ${PODCAST_ANIMATION.muteButtonEasing}
    ${({ isDynamic }) => (isDynamic ? `${PODCAST_ANIMATION.muteButtonDelay}s` : '0s')};
  will-change: transform;
`;

const StyledFallback = styled.div<{ bgColor: string }>`
  width: 100%;
  height: 100%;
  background-color: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  font-family: ${FONTS.primary};
  font-size: 20px;
  color: var(--card-text-primary, #181800);
`;

/**
 * Format duration in seconds to mm:ss
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const PodcastCard: FC<PodcastCardProps> = ({
  title,
  imageUrl,
  episodeThumbnail,
  duration,
  chapterTitle,
  backgroundColor,
  externalLink,
  isVideo,
  className,
  disableAnimation,
}) => {
  const { containerRef, isEntranceActive, isDynamicActive, onImageLoad } = useCardAnimation({
    threshold: 0.8,
    disabled: disableAnimation,
    entranceDuration: PODCAST_ANIMATION.entranceScaleDuration * 1000,
  });

  // Episode image for the large preview; falls back to show image
  const displayImage = episodeThumbnail ?? imageUrl;

  const podcastContent = (
    <>
      {/* Title only header — entrance phase */}
      <StyledHeader isAnimating={isEntranceActive}>
        <StyledTitle>{title}</StyledTitle>
      </StyledHeader>

      {/* Episode preview area — entrance + dynamic animations */}
      <StyledPreviewContainer isEntranceActive={isEntranceActive}>
        {/* Blurred background */}
        <StyledBlurredBackground bgImage={displayImage ?? undefined} isDynamic={isDynamicActive} />

        {/* Tape reel — behind cover, positioned absolutely */}
        {displayImage && (
          <StyledTapeReelContainer isDynamic={isDynamicActive}>
            <StyledTapeReelImage src={tapeReelImage} alt="" data-spinning={isDynamicActive ? 'true' : undefined} />
          </StyledTapeReelContainer>
        )}

        {/* Cover image — slides/rotates/scales in dynamic state */}
        <StyledCoverImage isDynamic={isDynamicActive} data-video={isVideo ? 'true' : undefined}>
          {displayImage ? (
            <StyledVideoImage src={displayImage} alt={title} loading="lazy" onLoad={onImageLoad} />
          ) : (
            <StyledFallback bgColor={backgroundColor ?? '#e8e4dc'}>{title}</StyledFallback>
          )}

          {/* Overlays on the cover */}
          {duration !== undefined && duration > 0 && <StyledDuration>{formatDuration(duration)}</StyledDuration>}
          {chapterTitle && <StyledChapterPill>{chapterTitle}</StyledChapterPill>}
        </StyledCoverImage>

        {/* Mute button — scales in after delay during dynamic state */}
        <StyledMuteButton isDynamic={isDynamicActive} aria-label="Mute">
          {/* TODO: Add mute icon SVG */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
          </svg>
        </StyledMuteButton>
      </StyledPreviewContainer>
    </>
  );

  return (
    <StyledPodcastContainer className={className} ref={containerRef}>
      {externalLink ? (
        <StyledLink href={externalLink} target="_blank" rel="noopener noreferrer">
          {podcastContent}
        </StyledLink>
      ) : (
        podcastContent
      )}
    </StyledPodcastContainer>
  );
};

export default PodcastCard;
